import { Cpu, HardDrive, MemoryStick, Monitor, Network, Tv } from 'lucide-react';
import { Card, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { DataList } from '@/components/ui/DataList';
import { EmptyState } from '@/components/ui/Feedback';
import { formatAbsolute, formatBytes, formatNumber, formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { DeviceHardware, DiskInventory } from '@/lib/api/types';

/*
  Hardware inventory.

  The disk rows are the only part with real judgement in them. Free space is what a technician
  is looking for when they open this tab, and a byte count alone does not answer "is this a
  problem": 8 GB free is fine on a 128 GB laptop and alarming on a 4 TB file server. So each
  disk shows a bar and a percentage, and the thresholds are named rather than being a colour
  gradient nobody can read precisely.

  The bar is decorative and aria-hidden. The numbers next to it carry the same information in
  text, and the row is a table row so a screen reader reads the header with each cell. A
  progressbar role here would announce "42 percent" without saying 42 percent of what.
*/

const LOW_SPACE_PERCENT = 10;
const TIGHT_SPACE_PERCENT = 20;

function freePercent(disk: DiskInventory): number {
  if (disk.totalBytes <= 0) {
    return 0;
  }
  return (disk.freeBytes / disk.totalBytes) * 100;
}

function spaceTone(percent: number): { readonly label: string; readonly bar: string; readonly text: string } {
  if (percent < LOW_SPACE_PERCENT) {
    return {
      label: 'Critically low',
      bar: 'bg-[var(--color-danger)]',
      text: 'text-[var(--color-danger)]',
    };
  }
  if (percent < TIGHT_SPACE_PERCENT) {
    return {
      label: 'Tight',
      bar: 'bg-[var(--color-warning)]',
      text: 'text-[var(--color-warning)]',
    };
  }
  return { label: 'Healthy', bar: 'bg-[var(--color-success)]', text: 'text-fg-muted' };
}

export function DeviceHardwarePanel({
  hardware,
}: {
  readonly hardware: DeviceHardware | null;
}): React.JSX.Element {
  if (hardware === null) {
    return (
      <Card>
        <EmptyState
          icon={<Cpu className="size-5" aria-hidden="true" />}
          title="No hardware inventory yet"
          description="The agent reports inventory on its first successful connection and then on a schedule. A device that has enrolled but never connected has nothing to show here yet."
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-fg-subtle">
        Collected{' '}
        <time dateTime={hardware.collectedAt} title={formatAbsolute(hardware.collectedAt)}>
          {formatRelative(hardware.collectedAt)}
        </time>
        . Inventory is a snapshot from the agent, not a live reading.
      </p>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                <Cpu className="size-4 text-fg-subtle" aria-hidden="true" />
                Processor
              </span>
            }
            headingLevel={3}
          />
          <div className="p-4">
            {hardware.cpu === null ? (
              <p className="text-sm text-fg-subtle italic">Not reported.</p>
            ) : (
              <DataList
                columns={2}
                items={[
                  { label: 'Model', value: hardware.cpu.model, span: 2 },
                  {
                    label: 'Cores',
                    value:
                      hardware.cpu.physicalCores === null
                        ? null
                        : `${formatNumber(hardware.cpu.physicalCores)} physical${
                            hardware.cpu.logicalCores === null
                              ? ''
                              : `, ${formatNumber(hardware.cpu.logicalCores)} logical`
                          }`,
                  },
                  {
                    label: 'Base clock',
                    value:
                      hardware.cpu.baseClockMhz === null
                        ? null
                        : `${(hardware.cpu.baseClockMhz / 1000).toFixed(2)} GHz`,
                  },
                  { label: 'Architecture', value: hardware.cpu.architecture, mono: true },
                ]}
              />
            )}
          </div>
        </Card>

        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                <MemoryStick className="size-4 text-fg-subtle" aria-hidden="true" />
                Memory
              </span>
            }
            headingLevel={3}
          />
          <div className="p-4">
            {hardware.memory === null ? (
              <p className="text-sm text-fg-subtle italic">Not reported.</p>
            ) : (
              <DataList
                columns={2}
                items={[
                  { label: 'Installed', value: formatBytes(hardware.memory.totalBytes) },
                  {
                    label: 'Available',
                    value:
                      hardware.memory.availableBytes === null
                        ? null
                        : formatBytes(hardware.memory.availableBytes),
                    emptyText: 'Not reported while offline',
                  },
                  {
                    label: 'Slots',
                    value:
                      hardware.memory.slotsUsed === null || hardware.memory.slotsTotal === null
                        ? null
                        : `${formatNumber(hardware.memory.slotsUsed)} of ${formatNumber(hardware.memory.slotsTotal)} populated`,
                  },
                ]}
              />
            )}
          </div>
        </Card>
      </div>

      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <HardDrive className="size-4 text-fg-subtle" aria-hidden="true" />
              Disks
            </span>
          }
          description="Free space thresholds: below 20 percent is tight, below 10 percent is critically low."
          headingLevel={3}
        />
        {hardware.disks.length === 0 ? (
          <p className="p-4 text-sm text-fg-subtle italic">No volumes reported.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[40rem] border-collapse text-left text-sm">
              <caption className="sr-only">
                Disk volumes on this device, with total capacity, free space and free
                percentage.
              </caption>
              <thead>
                <tr className="border-b border-border bg-surface-sunken">
                  {['Volume', 'Type', 'Capacity', 'Free', 'Free space', 'Encrypted'].map((head) => (
                    <th
                      key={head}
                      scope="col"
                      className="px-4 py-2 text-xs font-semibold tracking-wide text-fg-muted uppercase"
                    >
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {hardware.disks.map((disk) => {
                  const percent = freePercent(disk);
                  const tone = spaceTone(percent);
                  return (
                    <tr key={disk.mountPoint} className="border-b border-border last:border-b-0">
                      <th scope="row" className="px-4 py-2.5 align-top font-normal">
                        <span className="block font-mono text-[13px] font-medium text-fg">
                          {disk.mountPoint}
                        </span>
                        {disk.label === null ? null : (
                          <span className="block text-xs text-fg-subtle">{disk.label}</span>
                        )}
                      </th>
                      <td className="px-4 py-2.5 align-top">
                        <span className="text-fg-muted uppercase">{disk.media}</span>
                        {disk.fileSystem === null ? null : (
                          <span className="block text-xs text-fg-subtle">{disk.fileSystem}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 align-top whitespace-nowrap text-fg-muted tabular-nums">
                        {formatBytes(disk.totalBytes)}
                      </td>
                      <td
                        className={cn(
                          'px-4 py-2.5 align-top whitespace-nowrap font-medium tabular-nums',
                          tone.text,
                        )}
                      >
                        {formatBytes(disk.freeBytes)}
                      </td>
                      <td className="px-4 py-2.5 align-top">
                        <span className={cn('block text-xs font-semibold', tone.text)}>
                          {percent.toFixed(percent < 10 ? 1 : 0)} percent free, {tone.label}
                        </span>
                        <span
                          aria-hidden="true"
                          className="mt-1 block h-1.5 w-28 overflow-hidden rounded-full bg-surface-sunken"
                        >
                          <span
                            className={cn('block h-full rounded-full', tone.bar)}
                            style={{ width: `${String(Math.max(2, Math.min(100, percent)))}%` }}
                          />
                        </span>
                      </td>
                      <td className="px-4 py-2.5 align-top">
                        {disk.encrypted === null ? (
                          <span className="text-fg-subtle italic">Unknown</span>
                        ) : disk.encrypted ? (
                          <Badge tone="success">Encrypted</Badge>
                        ) : (
                          <Badge tone="warning">Not encrypted</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                <Monitor className="size-4 text-fg-subtle" aria-hidden="true" />
                Graphics
              </span>
            }
            headingLevel={3}
          />
          <div className="p-4">
            {hardware.gpus.length === 0 ? (
              <p className="text-sm text-fg-subtle italic">
                No display adapter reported. Common on a headless server, and it is why a
                session against one may have no screen to capture.
              </p>
            ) : (
              <ul className="flex flex-col gap-3">
                {hardware.gpus.map((gpu) => (
                  <li key={gpu.model}>
                    <DataList
                      columns={2}
                      items={[
                        { label: 'Adapter', value: gpu.model, span: 2 },
                        { label: 'Vendor', value: gpu.vendor },
                        {
                          label: 'Video memory',
                          value: gpu.vramBytes === null ? null : formatBytes(gpu.vramBytes),
                          emptyText: 'Shared with system memory',
                        },
                        { label: 'Driver', value: gpu.driverVersion, mono: true },
                      ]}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-2">
                <Tv className="size-4 text-fg-subtle" aria-hidden="true" />
                Monitors
              </span>
            }
            description="Scaling matters: a 200 percent display is captured at its native size and downscaled for the viewer."
            headingLevel={3}
          />
          <div className="p-4">
            {hardware.monitors.length === 0 ? (
              <p className="text-sm text-fg-subtle italic">
                No monitors attached. A session would have nothing to show.
              </p>
            ) : (
              <ul className="flex flex-col gap-2.5">
                {hardware.monitors.map((monitor) => (
                  <li key={monitor.name} className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="text-sm text-fg">{monitor.name}</span>
                    {monitor.primary ? <Badge tone="accent">Primary</Badge> : null}
                    <span className="text-xs text-fg-muted tabular-nums">
                      {monitor.widthPx === null || monitor.heightPx === null
                        ? 'Resolution not reported'
                        : `${formatNumber(monitor.widthPx)} by ${formatNumber(monitor.heightPx)}`}
                      {monitor.refreshHz === null ? '' : ` at ${formatNumber(monitor.refreshHz)} Hz`}
                      {monitor.scalingPercent === null
                        ? ''
                        : `, ${formatNumber(monitor.scalingPercent)} percent scaling`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>

      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <Network className="size-4 text-fg-subtle" aria-hidden="true" />
              Network adapters
            </span>
          }
          headingLevel={3}
        />
        {hardware.networkAdapters.length === 0 ? (
          <p className="p-4 text-sm text-fg-subtle italic">No adapters reported.</p>
        ) : (
          <ul>
            {hardware.networkAdapters.map((adapter) => (
              <li
                key={adapter.name}
                className="border-b border-border px-4 py-3 last:border-b-0"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-[13px] font-medium text-fg">{adapter.name}</span>
                  <Badge tone={adapter.up ? 'success' : 'neutral'}>
                    {adapter.up ? 'Up' : 'Down'}
                  </Badge>
                  {adapter.dhcp === null ? null : (
                    <Badge tone="neutral">{adapter.dhcp ? 'DHCP' : 'Static'}</Badge>
                  )}
                  {adapter.linkSpeedMbps === null ? null : (
                    <span className="text-xs text-fg-subtle tabular-nums">
                      {adapter.linkSpeedMbps >= 1000
                        ? `${formatNumber(adapter.linkSpeedMbps / 1000)} Gbps`
                        : `${formatNumber(adapter.linkSpeedMbps)} Mbps`}
                    </span>
                  )}
                </div>
                <p className="mt-1 font-mono text-xs text-fg-muted">
                  {[...adapter.ipv4, ...adapter.ipv6].length === 0
                    ? 'No address assigned'
                    : [...adapter.ipv4, ...adapter.ipv6].join(', ')}
                </p>
                {adapter.macAddress === null ? null : (
                  <p className="font-mono text-xs text-fg-subtle">MAC {adapter.macAddress}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
